import {v4 as uuidv4} from "uuid";
import {none, option} from "ts-option";
import BaseFormController from "../../../validation/BaseFormController";
import {FormField, OptionalFormField, RequiredFormField} from "../../../validation/FormField";
import FormValidators from "../../../validation/FormValidators";
import FormValidation from "../../../validation/FormValidation";
import UserData from "../../../model/user/UserData";
import Address from "../../../model/address/Address";
import Phone from "../../../model/phone/Phone";
import {UserRole} from "../../../model/user/UserRole";

export default class UserForm extends BaseFormController<UserForm> {

	public static createEmpty(): UserForm {
		return this.createUserForm({disabled: option(false)});
	}

	constructor(public readonly formId: string,
		public readonly firstName: RequiredFormField<string>,
		public readonly lastName: RequiredFormField<string>,
		public readonly username: RequiredFormField<string>,
		public readonly roles: RequiredFormField<string[]>,
		public readonly address: OptionalFormField<Address>,
		public readonly mobileNumber: RequiredFormField<Phone[]>,
		public readonly profileUrl: OptionalFormField<string>,
		public readonly brandId: OptionalFormField<string>,
		public readonly bioText: OptionalFormField<string>,
		public readonly photoId: OptionalFormField<string>,
		public readonly partnerBrandId: OptionalFormField<string>,
		public readonly licenceNumber: OptionalFormField<string>,
		public readonly disabled: RequiredFormField<boolean>,
		public readonly fieldWithFocus: FormField<string>) {
		super(fieldWithFocus, formId);
	}

	public static createFromUser(userData: UserData, disabled: boolean): UserForm {
		return this.createUserForm({
			firstName: option(userData.firstName),
			lastName: option(userData.lastName),
			username: option(userData.username),
			roles: option(userData.roles),
			address: userData.homeAddress,
			mobileNumber: userData.mobileNumber.map(mn => [mn]),
			profileUrl: userData.profileUrl,
			brandId: userData.brandId,
			bioText: userData.bioText,
			photoId: userData.photoId,
			partnerBrandId: userData.partnerBrandId,
			licenceNumber: userData.licenceNumber,
			disabled: option(disabled)
		});
	}

	private static createUserForm({
		firstName = none,
		lastName = none,
		username = none,
		roles = none,
		address = none,
		mobileNumber = none,
		profileUrl = none,
		brandId = none,
		bioText = none,
		photoId = none,
		partnerBrandId = none,
		licenceNumber = none,
		disabled = none
	}) {
		const firstNameField = RequiredFormField.create<string>(firstName, "First name is required", [
			FormValidators.nonEmptyString("First name is required")
		]);
		const isPBrandRequired = roles.map(
			roles => roles.some((r: string) => r === UserRole.ThirdPartyEngineer || r === UserRole.PartnerTechnician))
			.getOrElse(false);

		return new UserForm(
			uuidv4(),
			firstNameField,
			RequiredFormField.create<string>(lastName, "Last name is required", [
				FormValidators.nonEmptyString("Last name is required")
			]),
			RequiredFormField.create<string>(username, "Email is required", [FormValidators.email]),
			RequiredFormField.create<string[]>(roles, "Permission is required",
				[FormValidators.atLeastOneItem("At least one role is required")]),
			OptionalFormField.create<Address>(address),
			RequiredFormField.create<Phone[]>(mobileNumber, "Mobile number is required"),
			OptionalFormField.create<string>(profileUrl),
			OptionalFormField.create<string>(brandId,
				isPBrandRequired ? [FormValidators.nonEmptyString("Brand is required")] : []),
			OptionalFormField.create<string>(bioText),
			OptionalFormField.create<string>(photoId),
			OptionalFormField.create<string>(partnerBrandId,
				isPBrandRequired ? [FormValidators.nonEmptyString("Partner brand is required")] : []),
			OptionalFormField.create<string>(licenceNumber, [
				FormValidators.maxLength("Gas Safe Licence Card Number must be maximum 7 digits", 7),
				FormValidators.digitsOnly("Gas Safe Licence Card Number must contain digits only")
			]),
			RequiredFormField.create<boolean>(disabled),
			firstNameField
		);
	}

	onValid(callback: (data: UserData, password: string, disabled: boolean) => void) {
		// null is passed for permissions and organization name because it's irrelevant for creation of user
		const result = FormValidation.validateFields(
			[this.firstName, this.lastName, this.username, this.roles, this.disabled, this.brandId, this.partnerBrandId,
				this.licenceNumber, this.mobileNumber],
			() => {
				return new UserData(
					this.username.getValue(),
					this.firstName.getValue().trim(),
					this.lastName.getValue().trim(),
					this.roles.getValue(),
					null,
					null,
					this.address.getValue(),
					option(this.mobileNumber.getValue()[0]),
					this.profileUrl.getValue(),
					this.brandId.getValue(),
					none,
					this.bioText.getValue(),
					this.photoId.getValue(),
					this.partnerBrandId.getOptionalValue(),
					this.licenceNumber.getOptionalValue());
			}
		);
		result.onSuccess((data) => {
			callback(data, uuidv4(), this.disabled.getValue());
		});
	}

	copy({
		firstName = this.firstName,
		lastName = this.lastName,
		username = this.username,
		roles = this.roles,
		address = this.address,
		mobileNumber = this.mobileNumber,
		profileUrl = this.profileUrl,
		brandId = this.brandId,
		bioText = this.bioText,
		photoId = this.photoId,
		partnerBrandId = this.partnerBrandId,
		licenceNumber = this.licenceNumber,
		disabled = this.disabled
	}): UserForm {
		return new UserForm(this.formId,
			firstName,
			lastName,
			username,
			roles,
			address,
			mobileNumber,
			profileUrl,
			brandId,
			bioText,
			photoId,
			partnerBrandId,
			licenceNumber,
			disabled,
			this.fieldWithFocus);
	}

	fields(): Array<FormField<any>> {
		return [this.firstName, this.lastName, this.username, this.roles];
	}
}
